/* ============================================================
   CADtomie — Cross-subdomain cookie storage adapter
   ------------------------------------------------------------
   Supabase-js stores sessions in `localStorage` by default,
   which is origin-scoped — so `plan.cadtomie.com` can't see
   `intraop.cadtomie.com`'s session. To share a single session
   across both apps we hand Supabase a tiny Web-Storage shim
   that writes to a cookie scoped to `.cadtomie.com`.

   The adapter auto-detects its environment:

   • `*.cadtomie.com`   → cookies on `.cadtomie.com`, Secure+Lax
   • `*.cadtomie.local` → cookies on `.cadtomie.local` (dev with
                          a /etc/hosts entry, see ../README.md)
   • anything else      → falls back to `localStorage`

   The fallback means plain `localhost` dev keeps working
   without any setup — you just lose cross-subdomain sharing,
   which is fine because there's only one subdomain in dev.

   Designed for minimal moving parts: no BroadcastChannel locks,
   no token splitting. Supabase-js handles refresh-token
   rotation; with shared cookie storage all open tabs / apps
   pick up the rotated token automatically on next read.
   ============================================================ */

const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 30; // 30-day rolling window

interface CookieScope {
  /** Cookie `Domain=` attribute, e.g. `.cadtomie.com`. */
  domain: string;
  /** Whether to add the `Secure` flag (HTTPS-only). */
  secure: boolean;
}

/** Pick the right cookie scope for the current page, or null if we
 *  should fall back to localStorage. */
function detectScope(): CookieScope | null {
  if (typeof window === "undefined") return null;
  const { hostname, protocol } = window.location;

  if (hostname === "cadtomie.com" || hostname.endsWith(".cadtomie.com")) {
    return { domain: ".cadtomie.com", secure: protocol === "https:" };
  }
  if (hostname === "cadtomie.local" || hostname.endsWith(".cadtomie.local")) {
    return { domain: ".cadtomie.local", secure: false };
  }
  return null;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** In-memory fallback used by Supabase during SSR or when the page
 *  has no DOM (we never actually render server-side, but Supabase
 *  may probe storage during module init). */
function memoryStorage(): Storage {
  const m = new Map<string, string>();
  return {
    get length() { return m.size; },
    clear: () => m.clear(),
    key: (i: number) => Array.from(m.keys())[i] ?? null,
    getItem: (k: string) => m.get(k) ?? null,
    setItem: (k: string, v: string) => { m.set(k, v); },
    removeItem: (k: string) => { m.delete(k); },
  };
}

function makeCookieStorage(scope: CookieScope): Storage {
  return {
    // Supabase-js doesn't use `length` or `key()`, but the Web Storage
    // interface requires them. Return safe defaults.
    get length() { return 0; },
    clear: () => { /* no-op — Supabase only ever clears its own keys */ },
    key: () => null,

    getItem(key: string): string | null {
      const re = new RegExp("(?:^|; )" + escapeRegex(key) + "=([^;]+)");
      const match = document.cookie.match(re);
      return match ? decodeURIComponent(match[1]) : null;
    },

    setItem(key: string, value: string): void {
      const parts = [
        `${key}=${encodeURIComponent(value)}`,
        `Domain=${scope.domain}`,
        "Path=/",
        `Max-Age=${COOKIE_MAX_AGE_SEC}`,
        "SameSite=Lax",
      ];
      if (scope.secure) parts.push("Secure");
      document.cookie = parts.join("; ");
    },

    removeItem(key: string): void {
      const parts = [
        `${key}=`,
        `Domain=${scope.domain}`,
        "Path=/",
        "Max-Age=0",
        "SameSite=Lax",
      ];
      if (scope.secure) parts.push("Secure");
      document.cookie = parts.join("; ");
    },
  };
}

/** Returns the right storage backend for the current environment.
 *  Called once at module init by `supabaseClient.ts`. */
export function createCookieStorage(): Storage {
  const scope = detectScope();
  if (!scope) {
    // Plain localhost / IP / unknown host — fall back to localStorage
    // so dev keeps working without /etc/hosts gymnastics.
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage
      : memoryStorage();
  }
  return makeCookieStorage(scope);
}

/** Exposed for diagnostic / debug surfaces. */
export function describeStorage(): "cookie" | "localStorage" | "memory" {
  if (typeof window === "undefined") return "memory";
  if (detectScope()) return "cookie";
  if (window.localStorage) return "localStorage";
  return "memory";
}
